import { t, type ErrorCodeMapperEntry } from "./types.js";

/** Error codes 21000-21999. One thousand-block per file: a duplicate number here can only
 *  collide within this file, which is what makes the split safer than one shared object. */
export const portalChatErrors = {
  // 21xxx is a fresh block for the Portal Chat module (packages/server/portalChat/) — split off
  // SOMETHING_WENT_WRONG. This module had NO specific codes at all: every one of its 48 refusals sent
  // 9000, with the real text carried by a named constant in portalChat.constant.ts. The constants are
  // the unit of splitting here — one code per constant/concept, reused across all of that constant's
  // call sites.
  //
  // `CHAT_NOT_FOUND` — the conversation does not exist, OR the caller is no longer one of its members.
  // 13 call sites. The two causes deliberately share ONE code: `loadConversationForUser` collapses
  // them (`!loaded || !isMember(...)`) so a caller cannot probe which conversations exist. Do not
  // split this into a "not a member" code — the code itself would become the enumeration leak.
  PORTAL_CHAT_CONVERSATION_NOT_FOUND: {
    errorCode: 21001,
    errorMessage: t("Chat not found."),
    isDynamic: false,
  },
  // `READ_ONLY_MESSAGE` — the chat is read-only because its project is archived. 5 call sites.
  PORTAL_CHAT_READ_ONLY: {
    errorCode: 21002,
    errorMessage: t("This chat is read-only because its project is archived."),
    isDynamic: false,
  },
  // `NO_SEAT_MESSAGE` — the caller holds no seat on this portal, so chat is unavailable to them.
  // 4 call sites. A licensing state, not a permission denial, hence its own code.
  PORTAL_CHAT_NO_SEAT: {
    errorCode: 21003,
    errorMessage: t("You need a seat on this portal to use chat."),
    isDynamic: false,
  },
  // `SECTION_NOT_FOUND_MESSAGE` — the named chat section does not exist, or does not belong to this
  // user. 3 call sites (the section filters are owner-scoped, so a miss is "not found / not yours",
  // never a no-op write).
  PORTAL_CHAT_SECTION_NOT_FOUND: {
    errorCode: 21004,
    errorMessage: t("Section not found."),
    isDynamic: false,
  },
  // `MESSAGE_GONE_MESSAGE` — the target message was deleted or is otherwise gone. 2 call sites.
  PORTAL_CHAT_MESSAGE_NOT_AVAILABLE: {
    errorCode: 21005,
    errorMessage: t("This message is no longer available."),
    isDynamic: false,
  },
  // `REPLY_SOURCE_GONE_MESSAGE` — the message being replied to is gone. Distinct from
  // PORTAL_CHAT_MESSAGE_NOT_AVAILABLE because the UI names the reply source, not the message itself.
  PORTAL_CHAT_REPLY_SOURCE_NOT_AVAILABLE: {
    errorCode: 21006,
    errorMessage: t("The message you replied to is no longer available."),
    isDynamic: false,
  },
  // `FORWARD_SOURCE_GONE_MESSAGE` — the message being forwarded is gone. 2 call sites; the second is
  // really "you are not a member of the source chat", masked as gone on purpose (same anti-leak
  // reasoning as PORTAL_CHAT_CONVERSATION_NOT_FOUND) — keep both on this one code.
  PORTAL_CHAT_FORWARD_SOURCE_NOT_AVAILABLE: {
    errorCode: 21007,
    errorMessage: t("The message you forwarded is no longer available."),
    isDynamic: false,
  },
  // `INELIGIBLE_MEMBERS_MESSAGE` — some selected users are not active members of the portal.
  // 2 call sites (create conversation, add members).
  PORTAL_CHAT_INELIGIBLE_MEMBERS: {
    errorCode: 21008,
    errorMessage: t("Some selected users are not active members of this portal."),
    isDynamic: false,
  },
  // `EDIT_LOCKED_MESSAGE` — the message is deleted, a system message, or otherwise not editable.
  PORTAL_CHAT_MESSAGE_EDIT_LOCKED: {
    errorCode: 21009,
    errorMessage: t("This message can no longer be edited."),
    isDynamic: false,
  },
  // `DELETE_LOCKED_MESSAGE` — the message can no longer be deleted.
  PORTAL_CHAT_MESSAGE_DELETE_LOCKED: {
    errorCode: 21010,
    errorMessage: t("This message can no longer be deleted."),
    isDynamic: false,
  },
  // `ATTACHMENT_LOCKED_MESSAGE` — the attachment can no longer be deleted. 2 call sites (the second
  // fires for an awsKey that is not on the message; same user-facing text, so same code).
  PORTAL_CHAT_ATTACHMENT_DELETE_LOCKED: {
    errorCode: 21011,
    errorMessage: t("This attachment can no longer be deleted."),
    isDynamic: false,
  },
  // `EDIT_WINDOW_MESSAGE` — the 15-minute (`EDIT_WINDOW_MS`) edit window has expired.
  PORTAL_CHAT_EDIT_WINDOW_EXPIRED: {
    errorCode: 21012,
    errorMessage: t("Messages can only be edited within 15 minutes of sending."),
    isDynamic: false,
  },
  // `NEED_OTHER_MEMBER_MESSAGE` — a chat needs at least one member besides the caller.
  PORTAL_CHAT_NEED_OTHER_MEMBER: {
    errorCode: 21013,
    errorMessage: t("Select at least one other member."),
    isDynamic: false,
  },
  // `REMOVE_SELF_MESSAGE` — you cannot remove yourself from a chat (hide/mute it instead).
  PORTAL_CHAT_CANNOT_REMOVE_SELF: {
    errorCode: 21014,
    errorMessage: t("You can hide and mute a chat instead of leaving it."),
    isDynamic: false,
  },
  // `NOT_A_MEMBER_MESSAGE` — the user being removed is not a member of the chat.
  PORTAL_CHAT_USER_NOT_A_MEMBER: {
    errorCode: 21015,
    errorMessage: t("This user is not a member of the chat."),
    isDynamic: false,
  },
  // `SPACE_SECTION_MESSAGE` — project chats are ordered by project and cannot be filed into a
  // user-defined section.
  PORTAL_CHAT_PROJECT_CHAT_NOT_SECTIONABLE: {
    errorCode: 21016,
    errorMessage: t("Project chats are ordered by project and can't be filed in sections."),
    isDynamic: false,
  },
  // 21017-21022 replace the message constants in portalChat.constant.ts. They were missed by
  // the earlier passes because each site names a CONSTANT rather than a literal.
  PORTAL_CHAT_DIRECT_NEEDS_ONE_MEMBER: {
    errorCode: 21017,
    errorMessage: t("A direct chat has exactly one other member."),
    isDynamic: false,
  },
  PORTAL_CHAT_CREATE_RETRY: {
    errorCode: 21018,
    errorMessage: t("Could not start the chat. Please try again."),
    isDynamic: false,
  },
  PORTAL_CHAT_MESSAGE_EMPTY: {
    errorCode: 21019,
    errorMessage: t("A message needs text or an attachment."),
    isDynamic: false,
  },
  PORTAL_CHAT_RENAME_GROUP_ONLY: {
    errorCode: 21020,
    errorMessage: t("Only group chats can be renamed."),
    isDynamic: false,
  },
  PORTAL_CHAT_ADD_GROUP_ONLY: {
    errorCode: 21021,
    errorMessage: t("Members can only be added to group chats."),
    isDynamic: false,
  },
  PORTAL_CHAT_REMOVE_GROUP_ONLY: {
    errorCode: 21022,
    errorMessage: t("Members can only be removed from group chats."),
    isDynamic: false,
  },
} satisfies Record<string, ErrorCodeMapperEntry>;
